import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { BookOpen, ChevronRight, Sparkles, Moon, Sun } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const JournalModule = () => {
  const { t } = useTranslation();
  const [session, setSession] = useState(null); // 'morning' or 'evening'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showBreathing, setShowBreathing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [affirmation, setAffirmation] = useState('');
  const [streak, setStreak] = useState(0);
  const [todayEntry, setTodayEntry] = useState({ morning: null, evening: null });

  const API = process.env.REACT_APP_BACKEND_URL;

  // Get auth headers
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Load streak and today's entries
  useEffect(() => {
    loadStreak();
    loadTodayEntries();
  }, []);

  const loadStreak = async () => {
    try {
      const response = await axios.get(`${API}/api/journal/streak`, {
        headers: getAuthHeaders()
      });
      setStreak(response.data.streak || 0);
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadTodayEntries = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const morningResponse = await axios.get(
        `${API}/api/journal/entries/${today}/morning`,
        { headers: getAuthHeaders() }
      );
      
      const eveningResponse = await axios.get(
        `${API}/api/journal/entries/${today}/evening`,
        { headers: getAuthHeaders() }
      );
      
      setTodayEntry({
        morning: morningResponse.data.entry,
        evening: eveningResponse.data.entry
      });
    } catch (error) {
      console.error('Error loading today entries:', error);
    }
  };

  // Select random questions (avoiding recent ones)
  const selectQuestions = async (sessionType) => {
    try {
      // Get recent questions to avoid
      const recentResponse = await axios.get(
        `${API}/api/journal/recent-questions?session=${sessionType}`,
        { headers: getAuthHeaders() }
      );
      
      const recentPrompts = recentResponse.data.recent_prompts || [];
      const questionPool = t(`journal.${sessionType}.questions`, { returnObjects: true });
      
      // Filter out recent questions
      const availableQuestions = questionPool.filter(
        q => !recentPrompts.includes(q)
      );
      
      // Select 3 for morning, 4 for evening
      const count = sessionType === 'morning' ? 3 : 4;
      const shuffled = [...availableQuestions].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, Math.min(count, shuffled.length));
      
      // If not enough unique questions, add from pool
      if (selected.length < count) {
        const needed = count - selected.length;
        const remaining = questionPool.filter(q => !selected.includes(q));
        const shuffledRemaining = [...remaining].sort(() => Math.random() - 0.5);
        selected.push(...shuffledRemaining.slice(0, needed));
      }
      
      setSelectedQuestions(selected);
      
      // Select random affirmation
      const affirmations = t(`journal.${sessionType}.affirmations`, { returnObjects: true });
      const randomAffirmation = affirmations[Math.floor(Math.random() * affirmations.length)];
      setAffirmation(randomAffirmation);
      
    } catch (error) {
      console.error('Error selecting questions:', error);
      // Fallback to first N questions
      const count = sessionType === 'morning' ? 3 : 4;
      const questionPool = t(`journal.${sessionType}.questions`, { returnObjects: true });
      setSelectedQuestions(questionPool.slice(0, count));
      const affirmations = t(`journal.${sessionType}.affirmations`, { returnObjects: true });
      setAffirmation(affirmations[0]);
    }
  };

  // Start session
  const startSession = async (sessionType) => {
    setSession(sessionType);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setIsCompleted(false);
    
    // Show breathing animation for morning session
    if (sessionType === 'morning') {
      setShowBreathing(true);
      setTimeout(() => {
        setShowBreathing(false);
        selectQuestions(sessionType);
      }, 3000);
    } else {
      selectQuestions(sessionType);
    }
  };

  // Handle answer change
  const handleAnswerChange = (value) => {
    setAnswers({
      ...answers,
      [currentQuestionIndex]: value
    });
  };

  // Go to next question
  const handleNext = () => {
    if (currentQuestionIndex < selectedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      completeSession();
    }
  };

  // Go to previous question
  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Complete session and save
  const completeSession = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Prepare questions array
      const questions = selectedQuestions.map((prompt, index) => ({
        prompt,
        answer: answers[index] || ''
      }));
      
      const entryData = {
        date: today,
        session,
        questions,
        affirmation_id: affirmation,
        language: 'en'
      };
      
      await axios.post(`${API}/api/journal/entries`, entryData, {
        headers: getAuthHeaders()
      });
      
      setIsCompleted(true);
      loadStreak();
      loadTodayEntries();
      
      // Reset after showing affirmation
      setTimeout(() => {
        setSession(null);
        setCurrentQuestionIndex(0);
        setAnswers({});
        setSelectedQuestions([]);
      }, 5000);
      
    } catch (error) {
      console.error('Error saving journal entry:', error);
    }
  };

  // Breathing animation component
  const BreathingAnimation = () => (
    <motion.div 
      className="flex flex-col items-center justify-center h-[400px]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="w-32 h-32 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.7, 1, 0.7]
        }}
        transition={{
          duration: 3,
          repeat: 0,
          ease: "easeInOut"
        }}
      />
      <motion.p 
        className="mt-8 text-xl text-gray-700"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 3, repeat: 0 }}
      >
        {t('journal.morning.breathe')}
      </motion.p>
    </motion.div>
  );

  // Session selection view
  if (!session) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-3xl">
              <BookOpen className="w-8 h-8 text-emerald-600" />
              {t('journal.ui.title')}
            </CardTitle>
            <p className="text-gray-600 mt-2">
              {t('journal.ui.description')}
            </p>
            {streak > 0 && (
              <div className="flex items-center gap-2 mt-4">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                <span className="text-lg font-semibold text-gray-700">
                  {streak} {streak === 1 ? t('journal.ui.streak') : t('journal.ui.streakPlural')}
                </span>
              </div>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Morning Session Card */}
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${
                todayEntry.morning 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:border-emerald-400'
              }`}
              onClick={() => !todayEntry.morning && startSession('morning')}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Sun className="w-6 h-6 text-yellow-500" />
                      <h3 className="text-xl font-bold text-gray-800">
                        {t('journal.morning.title')}
                      </h3>
                    </div>
                    <p className="text-gray-600">
                      {t('journal.morning.subtitle')}
                    </p>
                    {todayEntry.morning && (
                      <p className="text-sm text-emerald-600 mt-2">
                        ✓ {t('journal.ui.alreadyCompleted')}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            {/* Evening Session Card */}
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${
                todayEntry.evening 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:border-blue-400'
              }`}
              onClick={() => !todayEntry.evening && startSession('evening')}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Moon className="w-6 h-6 text-blue-500" />
                      <h3 className="text-xl font-bold text-gray-800">
                        {t('journal.evening.title')}
                      </h3>
                    </div>
                    <p className="text-gray-600">
                      {t('journal.evening.subtitle')}
                    </p>
                    {todayEntry.evening && (
                      <p className="text-sm text-blue-600 mt-2">
                        ✓ {t('journal.ui.alreadyCompleted')}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-6 h-6 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Breathing animation view (morning only)
  if (showBreathing) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="bg-gradient-to-b from-emerald-50 to-white">
          <CardContent className="p-8">
            <BreathingAnimation />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completion/Affirmation view
  if (isCompleted) {
    return (
      <motion.div 
        className="max-w-2xl mx-auto p-6"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className={`${
          session === 'morning' 
            ? 'bg-gradient-to-b from-emerald-50 to-white' 
            : 'bg-gradient-to-b from-blue-900 to-black text-white'
        }`}>
          <CardContent className="p-12 text-center">
            <Sparkles className={`w-16 h-16 mx-auto mb-6 ${
              session === 'morning' ? 'text-yellow-500' : 'text-yellow-300'
            }`} />
            <h2 className={`text-2xl font-bold mb-4 ${
              session === 'evening' ? 'text-white' : 'text-gray-800'
            }`}>
              {affirmation}
            </h2>
            <p className={`text-lg ${
              session === 'evening' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {t('journal.ui.entrySaved')} ✓
            </p>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Question flow view
  const currentQuestion = selectedQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === selectedQuestions.length - 1;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card className={`${
        session === 'morning' 
          ? 'bg-gradient-to-b from-emerald-50 to-white' 
          : 'bg-gradient-to-b from-blue-900 to-black'
      }`}>
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${
              session === 'evening' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Question {currentQuestionIndex + 1} of {selectedQuestions.length}
            </span>
            <div className="flex gap-1">
              {selectedQuestions.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-8 h-1 rounded-full ${
                    idx <= currentQuestionIndex
                      ? session === 'morning' ? 'bg-emerald-500' : 'bg-blue-500'
                      : session === 'evening' ? 'bg-gray-700' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestionIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className={`text-xl font-semibold mb-4 ${
                session === 'evening' ? 'text-white' : 'text-gray-800'
              }`}>
                {currentQuestion}
              </h3>
              
              <Textarea
                value={answers[currentQuestionIndex] || ''}
                onChange={(e) => handleAnswerChange(e.target.value)}
                placeholder={t(`journal.${session}.placeholder`)}
                className={`min-h-[200px] text-lg ${
                  session === 'evening' 
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
                    : 'bg-white border-gray-300'
                }`}
                autoFocus
              />
            </motion.div>
          </AnimatePresence>
          
          <div className="flex justify-between gap-4">
            <Button
              onClick={handleBack}
              disabled={currentQuestionIndex === 0}
              variant="outline"
              className={session === 'evening' ? 'border-gray-700 text-white hover:bg-gray-800' : ''}
            >
              {t('journal.ui.back')}
            </Button>
            
            <Button
              onClick={handleNext}
              className={
                session === 'morning' 
                  ? 'bg-emerald-600 hover:bg-emerald-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }
            >
              {isLastQuestion ? t('journal.ui.complete') : t('journal.ui.next')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JournalModule;
